<template>
  <payments-section title="Charges" :count="loading ? null : totalCount" :default-open="defaultOpen">
    <template v-slot:actions>
      <div class="wp-filters">
        <button v-for="option in filterOptions" :key="option.value" type="button" class="wp-filter" :class="{ 'is-active': filter === option.value }" @click="filter = option.value">
          {{ option.label }}
        </button>
      </div>
    </template>

    <spinner v-if="loading"></spinner>
    <div v-else-if="rows.length === 0" class="wp-section-subtitle m-t-sm">No charges yet.</div>
    <table v-else class="wp-charges" :class="{ 'wp-charges--family': showWrestlers }">
      <thead>
        <tr>
          <th>Amount</th>
          <th>Status</th>
          <th>For</th>
          <th>Billed to</th>
          <th v-if="showWrestlers">Wrestler(s)</th>
          <th>Date</th>
          <th><span class="sr-only">Actions</span></th>
        </tr>
      </thead>
      <charge-row
        v-for="row in rows"
        :key="row.charge.id"
        :row="row.charge"
        :failures="row.failures"
        :date-range="row.dateRange"
        :show-wrestlers="showWrestlers"
        :show-refund-actions="isAdmin"
        :viewer-billable="isAdmin ? null : viewerBillable"
        :for-app-embed="forAppEmbed"
      ></charge-row>
    </table>
    <button v-if="nextPageUrl && !loading" type="button" class="wp-link-button wp-link-button--primary m-t-sm" :disabled="loadingMore" @click="loadMore">
      Show older
    </button>
    <div class="wp-section-subtitle m-t-sm">Insurance is charged separately and isn't listed here.</div>
    <p v-if="error" class="text-danger m-t-sm">{{ errorMessage }}</p>
  </payments-section>
</template>
<script>
import Spinner from './spinner.vue';
import ChargeRow from './charge_row.vue';
import PaymentsSection from './payments_section.vue';

// The payments dashboard's charge feed for one wrestler (or, with
// showWrestlers, a parent's whole family), using the same row.
// Nightly retries of the same invoice collapse into one "Failed ×N" row.
export default {
  name: 'wrestler-charges-feed',
  components: { Spinner, ChargeRow, PaymentsSection },
  props: {
    chargesUrl: { type: String, required: true },
    // The family view covers several kids, so it gets the Wrestler(s) column
    showWrestlers: { type: Boolean, default: false },
    // payments-section's: null opens it when there are charges
    defaultOpen: { type: Boolean, default: null },
    isAdmin: { type: Boolean, default: false },
    viewerBillable: { type: Object, default: null },
    forAppEmbed: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      loadingMore: false,
      error: false,
      errorMessage: '',
      charges: [],
      nextPageUrl: null,
      totalCount: 0,
      filter: 'all',
      filterOptions: [
        { label: 'All', value: 'all' },
        { label: 'Paid', value: 'successful' },
        { label: 'Failed', value: 'failed' },
      ],
    };
  },
  computed: {
    rows() {
      const charges = this.filter === 'all' ? this.charges : this.charges.filter((c) => c.status === this.filter);
      const rows = [];
      charges.forEach((charge) => {
        const previous = _.last(rows);
        // Newest first, so a run of failures for the same invoice folds into the newer row
        if (charge.status === 'failed' && previous && previous.charge.status === 'failed' && this.sameChargeable(previous.charge, charge)) {
          previous.failures += 1;
          previous.dateRange = this.dateRange(charge.created_at, previous.charge.created_at);
          return;
        }
        rows.push({ charge, failures: 1, dateRange: null });
      });
      return rows;
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      axios.get(`${this.chargesUrl}?per_page=20`)
        .then((response) => {
          this.charges = response.data.charges;
          this.nextPageUrl = this.$apiService.nextPageUrl(response.headers);
          this.totalCount = Number(response.headers.totalcount) || this.charges.length;
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          this.error = true;
          this.errorMessage = `Error loading charges ${error.toString()}`;
        });
    },
    loadMore() {
      this.loadingMore = true;
      axios.get(this.nextPageUrl)
        .then((response) => {
          this.charges = this.charges.concat(response.data.charges);
          this.nextPageUrl = this.$apiService.nextPageUrl(response.headers);
          this.loadingMore = false;
        })
        .catch((error) => {
          this.loadingMore = false;
          this.error = true;
          this.errorMessage = `Error loading charges ${error.toString()}`;
        });
    },
    sameChargeable(a, b) {
      return a.chargeable.type === b.chargeable.type && a.chargeable.id === b.chargeable.id;
    },
    shortDate(value) {
      const date = moment(value);
      return date.year() === moment().year() ? date.format('MMM D') : date.format('MMM D, YYYY');
    },
    dateRange(oldest, newest) {
      const from = moment(oldest);
      const to = moment(newest);
      if (from.isSame(to, 'day')) {
        return this.shortDate(newest);
      }
      if (from.isSame(to, 'month')) {
        // Same as shortDate: the year only when it isn't this year
        const year = to.year() === moment().year() ? '' : `, ${to.format('YYYY')}`;
        return `${from.format('MMM D')}–${to.format('D')}${year}`;
      }
      return `${this.shortDate(oldest)} – ${this.shortDate(newest)}`;
    },
  },
};
</script>
